import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil, finalize } from 'rxjs';
import { WishlistService } from '../../services/wishlist.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-wishlist-heart',
  standalone: true,
  imports: [CommonModule],
  template: `
    <button
      class="wishlist-heart"
      [class.in-wishlist]="isInWishlist"
      [class.loading]="isLoading"
      [class.disabled]="!canUseWishlist"
      [disabled]="!canUseWishlist || isLoading"
      (click)="toggleWishlist($event)"
      [title]="getTooltipText()"
      type="button"
    >
      <svg
        viewBox="0 0 24 24"
        class="heart-icon"
        [class.filled]="isInWishlist"
        [class.pulsing]="isLoading"
      >
        <path
          d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
          [attr.fill]="isInWishlist ? 'currentColor' : 'none'"
          [attr.stroke]="'currentColor'"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>

      <div *ngIf="isLoading" class="loading-spinner"></div>

      <span class="sr-only">
        {{ isInWishlist ? 'Remove from wishlist' : 'Add to wishlist' }}
      </span>
    </button>
  `,
  styles: [
    `
      .wishlist-heart {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        border: none;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.9);
        backdrop-filter: blur(4px);
        cursor: pointer;
        transition: all 0.2s ease;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        z-index: 10;
      }

      .wishlist-heart:hover:not(.disabled):not(.loading) {
        background: rgba(255, 255, 255, 1);
        transform: scale(1.05);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }

      .wishlist-heart:active:not(.disabled):not(.loading) {
        transform: scale(0.95);
      }

      .wishlist-heart.disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .wishlist-heart.loading {
        cursor: wait;
      }

      .heart-icon {
        width: 20px;
        height: 20px;
        color: #dc3545;
        transition: all 0.2s ease;
      }

      .heart-icon.filled {
        color: #dc3545;
        filter: drop-shadow(0 0 4px rgba(220, 53, 69, 0.3));
      }

      .heart-icon.pulsing {
        animation: pulse 1s infinite;
      }

      .wishlist-heart.in-wishlist .heart-icon {
        animation: heartBeat 0.6s ease;
      }

      .loading-spinner {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 14px;
        height: 14px;
        border: 2px solid #f3f3f3;
        border-top: 2px solid #dc3545;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }

      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      @keyframes pulse {
        0%,
        100% {
          opacity: 1;
          transform: scale(1);
        }
        50% {
          opacity: 0.7;
          transform: scale(1.05);
        }
      }

      @keyframes heartBeat {
        0% {
          transform: scale(1);
        }
        25% {
          transform: scale(1.2);
        }
        50% {
          transform: scale(1);
        }
        75% {
          transform: scale(1.1);
        }
        100% {
          transform: scale(1);
        }
      }

      @keyframes spin {
        0% {
          transform: translate(-50%, -50%) rotate(0deg);
        }
        100% {
          transform: translate(-50%, -50%) rotate(360deg);
        }
      }

      @media (max-width: 768px) {
        .wishlist-heart {
          width: 36px;
          height: 36px;
          touch-action: manipulation;
        }

        .heart-icon {
          width: 18px;
          height: 18px;
        }
      }
    `,
  ],
})
export class WishlistHeartComponent implements OnInit, OnDestroy {
  @Input() listingId!: string;
  @Input() tableName!: string;
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  @Input() variant: 'default' | 'minimal' = 'default';

  @Output() wishlistChanged = new EventEmitter<{
    listingId: string;
    isInWishlist: boolean;
    action: 'added' | 'removed';
  }>();

  isInWishlist: boolean = false;
  isLoading: boolean = false;
  canUseWishlist: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    private wishlistService: WishlistService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.canUseWishlist = this.wishlistService.canUseWishlist();

    if (this.canUseWishlist) {
      this.wishlistService.wishlistStatus$
        .pipe(takeUntil(this.destroy$))
        .subscribe((wishlistStatus) => {
          const status = wishlistStatus[this.listingId];
          this.isInWishlist = status?.in_wishlist || false;
        });
    }

    this.authService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.canUseWishlist = !!user;
        if (!user) {
          this.isInWishlist = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleWishlist(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (
      !this.canUseWishlist ||
      this.isLoading ||
      !this.listingId ||
      !this.tableName
    ) {
      return;
    }

    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    const previousState = this.isInWishlist;

    this.isInWishlist = !this.isInWishlist;

    this.wishlistService
      .toggleWishlist(this.listingId, this.tableName)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (response) => {
          this.isInWishlist = response.data.in_wishlist;

          this.wishlistChanged.emit({
            listingId: this.listingId,
            isInWishlist: this.isInWishlist,
            action: response.data.action,
          });

          this.showFeedback(response.data.action);
        },
        error: (error) => {
          this.isInWishlist = previousState;
          console.error('Wishlist toggle failed:', error);
          this.showErrorFeedback(error.message);
        },
      });
  }

  getTooltipText(): string {
    if (!this.canUseWishlist) {
      return 'Sign in to save items to your wishlist';
    }

    if (this.isLoading) {
      return 'Processing...';
    }

    return this.isInWishlist ? 'Remove from wishlist' : 'Add to wishlist';
  }

  private showFeedback(action: 'added' | 'removed'): void {
    const message =
      action === 'added' ? 'Added to wishlist' : 'Removed from wishlist';
  }

  private showErrorFeedback(message: string): void {
    console.error('Wishlist error:', message);
  }
}
